import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import {
  evidenceRuleLabel,
  formatEvidenceValue,
} from '../stockEvidencePresentation';

const hasCustomerValue = (value) => (
  value !== null
  && value !== undefined
  && String(value).trim() !== ''
  && String(value).trim() !== 'No description available'
);

const safeCompanyName = (stock) => (
  stock?.company_name || stock?.companyName || 'Company information'
);

const lineageValue = (lineage, snakeCase, camelCase) => (
  lineage?.[snakeCase] || lineage?.[camelCase] || null
);

/**
 * Lazy, customer-safe context for a single selected Stock Focus company.
 * Both data sources are stored Pinngro authorities: stocks and retained
 * published screening evidence. This component never contacts a provider.
 */
export default function StockFocusCompanyDrawer({
  stock,
  stockFocus,
  lineage,
  axiosInstance,
  onClose,
}) {
  const dialogRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [evidenceError, setEvidenceError] = useState('');

  const symbol = String(stock?.symbol || '').trim().toUpperCase();
  const generationId = lineageValue(lineage, 'generation_id', 'generationId');
  const generationHash = lineageValue(lineage, 'generation_hash', 'generationHash');

  useEffect(() => {
    if (!symbol) return undefined;
    let current = true;
    setLoading(true);
    setCompany(null);
    setEvidence(null);
    setEvidenceError('');
    dialogRef.current?.focus();

    const load = async () => {
      const companyRequest = axiosInstance.post('/api/stocks/info/batch', { symbols: [symbol] });
      const evidenceRequest = stockFocus && generationId && generationHash
        ? axiosInstance.get(`/api/stocks/evidence/${encodeURIComponent(symbol)}`, {
          params: {
            stock_focus: stockFocus,
            generation_id: generationId,
            generation_hash: generationHash,
          },
        })
        : Promise.resolve(null);
      const [companyResult, evidenceResult] = await Promise.allSettled([
        companyRequest,
        evidenceRequest,
      ]);
      if (!current) return;
      if (companyResult.status === 'fulfilled') {
        const records = Array.isArray(companyResult.value?.data) ? companyResult.value.data : [];
        setCompany(records.find((item) => String(item?.symbol || '').toUpperCase() === symbol) || null);
      }
      if (evidenceResult?.status === 'fulfilled' && evidenceResult.value) {
        setEvidence(evidenceResult.value.data);
      } else if (evidenceResult?.status === 'rejected') {
        setEvidenceError('Current Stock Focus evidence is unavailable right now.');
      }
      setLoading(false);
    };
    load().catch(() => {
      if (current) {
        setLoading(false);
        setEvidenceError('Company context is unavailable right now.');
      }
    });
    return () => { current = false; };
  }, [axiosInstance, generationHash, generationId, stockFocus, symbol]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!symbol) return null;
  const classification = evidence?.classification || {};
  const displayName = company?.company_name || evidence?.company_name || safeCompanyName(stock);
  const rules = Array.isArray(evidence?.match?.rules) ? evidence.match.rules : [];

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-slate-950/50 p-0 sm:p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-company-context-title"
        tabIndex="-1"
        className="flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white p-5 shadow-2xl outline-none dark:bg-slate-950 sm:rounded-3xl sm:p-7"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-700 dark:text-blue-300">Company context</p>
            <h2 id="stock-company-context-title" className="mt-1 break-words text-2xl font-black">{displayName}</h2>
            <p className="mt-1 text-lg font-bold text-slate-600 dark:text-slate-300">{symbol}</p>
          </div>
          <button type="button" onClick={onClose} className="min-h-11 min-w-11 rounded-xl border border-slate-300 p-3 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800" aria-label="Close company information">
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        {loading ? <p className="mt-6 text-sm text-slate-600 dark:text-slate-300">Loading stored company information…</p> : (
          <>
            {hasCustomerValue(company?.description) && <p className="mt-6 text-sm leading-6 text-slate-700 dark:text-slate-200">{company.description}</p>}
            {(hasCustomerValue(classification.sector) || hasCustomerValue(classification.industry) || hasCustomerValue(company?.domain)) && (
              <dl className="mt-6 grid gap-4 border-y border-slate-200 py-5 text-sm dark:border-slate-800">
                {hasCustomerValue(classification.sector) && <div><dt className="font-black text-slate-500">Sector</dt><dd className="mt-1">{classification.sector}</dd></div>}
                {hasCustomerValue(classification.industry) && <div><dt className="font-black text-slate-500">Industry</dt><dd className="mt-1">{classification.industry}</dd></div>}
                {hasCustomerValue(company?.domain) && <div><dt className="font-black text-slate-500">Website</dt><dd className="mt-1 break-all">{company.domain}</dd></div>}
              </dl>
            )}
            {stockFocus && (
              <details className="mt-6 rounded-2xl border border-blue-200 p-4 dark:border-blue-900" open>
                <summary className="cursor-pointer font-black">Why it matches {stockFocus}</summary>
                {evidence?.match?.matched && <p className="mt-3 text-sm leading-6">This company meets the current published Stock Focus criteria. It is a qualifying candidate, not a recommendation or ranked pick.</p>}
                {rules.length > 0 && <ul className="mt-3 space-y-2 text-sm">{rules.filter((rule) => rule.matched).map((rule) => <li key={`${rule.field}-${rule.operator}`} className="flex gap-2"><span aria-hidden="true">✓</span><span>{rule.explanation || evidenceRuleLabel(rule)}</span></li>)}</ul>}
                {!loading && !evidence && !evidenceError && <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">Exact Stock Focus evidence is available when this selection has a published candidate lineage.</p>}
                {evidenceError && <p className="mt-3 text-sm text-amber-800 dark:text-amber-200">{evidenceError}</p>}
                {rules.length > 0 && <details className="mt-4"><summary className="cursor-pointer font-bold text-blue-700 dark:text-blue-300">View detailed evidence</summary><dl className="mt-3 space-y-3 text-sm">{rules.map((rule) => <div key={`${rule.field}-${rule.operator}`}><dt className="font-black">{evidenceRuleLabel(rule)}</dt><dd className="mt-1 text-slate-600 dark:text-slate-300">{`${rule.operator} ${formatEvidenceValue(rule.field, rule.actual_value)}; required ${formatEvidenceValue(rule.field, rule.threshold)}`}</dd></div>)}</dl>{hasCustomerValue(evidence?.evidence?.session_date) && <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">Evidence session: {evidence.evidence.session_date}</p>}{evidence?.growth_momentum_lineage?.applicable && <dl className="mt-4 grid gap-3 border-t border-slate-200 pt-4 text-sm dark:border-slate-800"><div><dt className="font-black">126-session close return</dt><dd>{evidence.growth_momentum_lineage.derived_return_percent == null ? 'Unavailable' : `${evidence.growth_momentum_lineage.derived_return_percent}%`}</dd></div><div><dt className="font-black">Derivation</dt><dd>{evidence.growth_momentum_lineage.derivation_version || 'Unavailable'}</dd></div><div><dt className="font-black">Adjustment boundaries</dt><dd>Dividends excluded · provider adjusted_close not used</dd></div></dl>}</details>}
              </details>
            )}
          </>
        )}
      </section>
    </div>
  );
}
